import React, { useState } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import authService from '../services/authService';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotIsError, setForgotIsError] = useState(false);

  const handleInputChange = (e) => {
    if (error) setError('');
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotLoading(true);
    setForgotMessage('');
    setForgotIsError(false);
    try {
      const data = await authService.forgotPassword(forgotEmail);
      setForgotMessage(data.message || 'Your password has been sent to your email.');
      setForgotIsError(false);
    } catch (err) {
      setForgotMessage(err.error || 'Failed to process request');
      setForgotIsError(true);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.email || !formData.password || !formData.role) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);

    try {
      const data = await authService.login(formData.email, formData.password, formData.role);

      // Save token, refresh token, and user data
      login(data.access_token, data.refresh_token, data.user);

      // Redirect: honour returnTo if present, otherwise go to role dashboard
      const role = data.user.role.toLowerCase();
      const returnTo = location.state?.returnTo;
      navigate(returnTo || `/dashboard/${role}`);
    } catch (err) {
      setError(err.error || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={8} lg={5}>
          <Card className="shadow-lg">
            <Card.Header className="bg-primary text-white text-center py-3">
              <h3 className="mb-0">
                <i className="fas fa-sign-in-alt me-2"></i>
                Login
              </h3>
              <small>Equipment Inspection System</small>
            </Card.Header>
            <Card.Body className="p-4">
              {showForgotPassword ? (
                <>
                  <h5 className="mb-3">
                    <i className="fas fa-key me-2"></i>
                    Forgot Password
                  </h5>
                  {forgotMessage && (
                    <Alert variant={forgotIsError ? 'danger' : 'success'} dismissible onClose={() => setForgotMessage('')}>
                      {forgotMessage}
                    </Alert>
                  )}
                  <Form onSubmit={handleForgotPassword}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <i className="fas fa-envelope me-2"></i>
                        Email Address <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Enter your registered email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        required
                      />
                      <Form.Text className="text-muted">
                        If this email is registered, your existing password will be emailed to it.
                      </Form.Text>
                    </Form.Group>
                    <div className="d-grid gap-2">
                      <Button variant="primary" type="submit" disabled={forgotLoading}>
                        {forgotLoading
                          ? <><span className="spinner-border spinner-border-sm me-2"></span>Sending...</>
                          : <><i className="fas fa-paper-plane me-2"></i>Send Password</>}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={() => { setShowForgotPassword(false); setForgotMessage(''); setForgotEmail(''); }}
                      >
                        <i className="fas fa-arrow-left me-2"></i>Back to Login
                      </Button>
                    </div>
                  </Form>
                </>
              ) : (
              <>
              {location.state?.accessDenied && (
                <Alert variant="warning">
                  <i className="fas fa-ban me-2"></i>
                  <strong>Access Denied.</strong> Only Admins are allowed to access the Training Module.
                </Alert>
              )}

              {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                  <i className="fas fa-exclamation-circle me-2"></i>
                  {error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <i className="fas fa-envelope me-2"></i>
                    Email Address <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <i className="fas fa-lock me-2"></i>
                    Password <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                    />
                    <Button
                      variant="link"
                      className="position-absolute end-0 top-0 text-secondary"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ textDecoration: 'none' }}
                    >
                      <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </Button>
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <i className="fas fa-user-tag me-2"></i>
                    Select Your Role <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">-- Select Role --</option>
                    <option value="jr_inspector">Inspector</option>
                    <option value="sr_inspector">Reviewer</option>
                    <option value="admin">Admin</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    <i className="fas fa-info-circle me-1"></i>
                    All three fields (Email, Password, and Role) must be correct
                  </Form.Text>
                </Form.Group>

                <Button
                  variant="primary"
                  type="submit"
                  className="w-100 py-2 mb-3"
                  disabled={loading}
                  size="lg"
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Logging in...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-sign-in-alt me-2"></i>
                      Login
                    </>
                  )}
                </Button>

                <div className="text-center">
                  <small className="text-muted">
                    Forgot your password?{' '}
                    <a href="#" className="text-primary" onClick={(e) => { e.preventDefault(); setShowForgotPassword(true); }}>
                      Reset it here
                    </a>
                  </small>
                </div>
              </Form>
              </>
              )}
            </Card.Body>
            <Card.Footer className="text-center py-3 bg-light">
              <small>
                Don't have an account?{' '}
                <a href="/register" className="text-primary fw-bold">
                  Register here
                </a>
              </small>
            </Card.Footer>
          </Card>

          <div className="text-center mt-3">
            <Button variant="outline-secondary" onClick={() => navigate('/')}>
              <i className="fas fa-home me-2"></i>
              Back to Home
            </Button>
          </div>

          {/* Role Information Card */}
          <Card className="mt-3 border-info">
            <Card.Body className="p-3">
              <h6 className="text-info mb-2">
                <i className="fas fa-info-circle me-2"></i>
                Role Information
              </h6>
              <small className="d-block mb-1">
                <strong>Inspector:</strong> Fill and submit inspection forms
              </small>
              <small className="d-block mb-1">
                <strong>Reviewer:</strong> Review and approve inspector submissions
              </small>
              <small className="d-block mb-1">
                <strong>Admin:</strong> Final approval and certificate generation, and training module access
              </small>
              <small className="d-block text-muted">
                <i className="fas fa-exclamation-circle me-1"></i>
                Reviewers may also log in as Inspector to submit forms — these still go through the normal review step.
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Login;
